import { ReactNode, useCallback, useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { Button, InputGroup, InputGroupText, UncontrolledTooltip } from 'src/components/ui';
import { Trans } from '@lingui/react/macro';
import { DataRemark } from '../../../api/DataRemarksApi';
import UWEFormContext from '../../../context/UWEFormContext';
import ObjectTableContext from '../ObjectTableContext';
import FieldComparisonBtn from '../../FieldComparisonBtn';
import { FormContext } from '../../FormComponentTypes';
import { JSONSchema } from '../../../util/jsonschemaTypes';
import { getActiveDataRemarkForFieldSchema } from '../../../util/extractFormFieldsFromSchema';
import { useDarkMode } from '../../../context/DarkModeContext';
import useInterpolatedText from './useInterpolatedText';
import Icon from 'src/components/Icon';

type WrapInputArgs = {
  input: ReactNode;
  fieldId?: string;
  formData?: any;
  fieldSchema?: JSONSchema;
  formContext?: FormContext;
  prefix?: string;
  sufix?: string;
  tooltipText?: string;
  rootFormData?: any;
  dataPath?: string;
  dataRemark?: DataRemark | null;
  conflictWarning?: boolean;
  onClearConflict?: () => void;
};

function useWrapInput({
  input,
  prefix,
  sufix,
  tooltipText,
  rootFormData,
  dataPath,
  fieldId,
  fieldSchema,
  formContext,
  formData,
  conflictWarning,
  onClearConflict,
}: WrapInputArgs) {
  const UweFormContext = useContext(UWEFormContext);
  const objectTableAddonWidths = useContext(ObjectTableContext);
  const { isDarkMode } = useDarkMode();
  const {
    currentEntity = null,
    dataRemarksById = {},
    showPrefix = true,
    showSuffix = true,
    showPercentDiff = false,
    pastReports = [],
    loadingPastReports = false,
    errorLoadingPastReports,
  } = UweFormContext ?? {};

  const dataRemark = useMemo(() => getActiveDataRemarkForFieldSchema(dataRemarksById, fieldId), [dataRemarksById, fieldId]);
  const tooltipBindings = useMemo(() => ({ currentEntity }), [currentEntity]);
  const evaluatedTooltipText = useInterpolatedText(tooltipText, rootFormData, dataPath, formContext, tooltipBindings);

  const prefixRef = useRef<HTMLSpanElement>(null);
  const suffixRef = useRef<HTMLSpanElement>(null);
  const hasPrefix = showPrefix && !!prefix;
  const hasSuffix = showSuffix && !!sufix;

  // Report this field's own prefix/suffix width to the enclosing ObjectTable (if any)
  // so every prefix in the table can share the widest one's width, and separately for
  // suffixes. Measuring via scrollWidth (not a cached/computed value) means this still
  // reflects the natural content width even once the shared max width has been applied.
  useLayoutEffect(() => {
    if (!objectTableAddonWidths || !hasPrefix || !prefixRef.current) return;
    objectTableAddonWidths.reportPrefixWidth(prefixRef.current.scrollWidth);
  }, [objectTableAddonWidths, prefix, hasPrefix]);

  useLayoutEffect(() => {
    if (!objectTableAddonWidths || !hasSuffix || !suffixRef.current) return;
    objectTableAddonWidths.reportSuffixWidth(suffixRef.current.scrollWidth);
  }, [objectTableAddonWidths, sufix, hasSuffix]);

  const tooltip = () => {
    if (evaluatedTooltipText) {
      const lines = evaluatedTooltipText.split('\n');
      return (
        <InputGroupText style={{ background: isDarkMode ? '#32363d' : 'white' }} className="input-group-append" id={`tooltip-${fieldId}`}>
          <Icon icon="Question" className="text-input-tooltip-icon fa fa-regular" style={{ color: isDarkMode ? '#878b9a' : 'gray' }} />
          <UncontrolledTooltip placement="top" target={`tooltip-${fieldId}`} trigger="hover" fade={false}>
            {lines.map((ln, ix) => (
              <div key={ln + ix} className="mb-2">
                <small>
                  <em style={{ fontWeight: 500 }}>{ix + 1}.</em>
                </small>{' '}
                {ln}
              </div>
            ))}
          </UncontrolledTooltip>
        </InputGroupText>
      );
    }
    return null;
  };

  const conflictWarningTooltip = useCallback(() => {
    if (!conflictWarning) return null;
    const triggerId = `conflict-warning-${fieldId}`;

    return (
      <InputGroupText className="input-group-append cursor-pointer" id={triggerId}>
        <Icon icon="Warning" className="text-warning" />
        <UncontrolledTooltip placement="top" target={triggerId} trigger="click" fade={true} autohide={true}>
          <div className="flex flex-col gap-2">
            <span>
              <Trans>The reported value does not match our calculation</Trans>
            </span>
            {onClearConflict ? (
              <Button size="sm" color="success" outline onClick={onClearConflict}>
                <Trans>Use calculated value</Trans>
              </Button>
            ) : null}
          </div>
        </UncontrolledTooltip>
      </InputGroupText>
    );
  }, [fieldId, conflictWarning, onClearConflict]);

  // TODO: Having an onclick that focuses on the data remark card would be really good
  const dataRemarkTooltip = () => {
    if (dataRemark) {
      const { message } = dataRemark;
      return (
        <InputGroupText className="input-group-append" id={`data-remark-${fieldId}`}>
          <Icon icon="fa fa-flag" className="text-input-data-remark-icon fa text-danger" />
          {message ? (
            <UncontrolledTooltip placement="top" target={`data-remark-${fieldId}`} trigger="hover" fade={false}>
              {message}
            </UncontrolledTooltip>
          ) : null}
        </InputGroupText>
      );
    }
    return null;
  };

  if (!prefix && !sufix && !dataRemark && !showPercentDiff && !conflictWarning && !evaluatedTooltipText) return input;

  const prefixWidth = objectTableAddonWidths?.maxPrefixWidth;
  const suffixWidth = objectTableAddonWidths?.maxSuffixWidth;

  return (
    <div className="input-wrapper">
      <InputGroup className="mb-0" style={{ flexWrap: 'nowrap' }}>
        {hasPrefix ? (
          <InputGroupText
            ref={prefixRef}
            className="input-group-prepend text-xs"
            style={prefixWidth ? { width: prefixWidth, minWidth: prefixWidth } : undefined}
          >
            {prefix}
          </InputGroupText>
        ) : null}
        {input}
        {tooltip()}
        {showPercentDiff ? (
          <FieldComparisonBtn
            currentEntity={currentEntity}
            fieldId={fieldId}
            pastReports={pastReports}
            loadingPastReports={loadingPastReports}
            errorLoadingPastReports={errorLoadingPastReports}
            formContext={formContext!}
            formData={formData}
            fieldSchema={fieldSchema!}
          />
        ) : null}
        {conflictWarningTooltip()}
        {dataRemarkTooltip()}
        {hasSuffix ? (
          <InputGroupText
            ref={suffixRef}
            className="input-group-append text-xs"
            style={suffixWidth ? { width: suffixWidth, minWidth: suffixWidth } : undefined}
          >
            {sufix}
          </InputGroupText>
        ) : null}
      </InputGroup>
    </div>
  );
}

export default useWrapInput;
